import classnames from "classnames";
import React, { PureComponent, ReactNode } from "react";
import ScrollLock, { TouchScrollable } from "react-scrolllock"; // TODO: WP-1418 - replace by body-scroll-lock package
import withRenderOn from "src/hoc/withRenderOn";
import { IRenderOn } from "src/hooks/useRenderOn";
import styled from "styled-components";

import { QuantityPicker, ToggleSlide } from "../../..";
import { IProductRemove } from "../../../../hoc/persister/cartProductRemove/cartProductRemove";
import { ICartProductUpdate } from "../../../../hoc/persister/cartProductUpdate/cartProductUpdate";
import { cartTotal } from "../../../../hoc/services/cart";
import history from "../../../../lib/history";
import { formatPrice } from "../../../../services/number.service";
import { translate } from "../../../../services/translation.service";
import theme, { media } from "../../../../theme";
import { ICart, ICartProductDetail } from "../../../../types/cart";
import { IProduct } from "../../../../types/product";
import Button from "../../../Button";
import CircularLoader from "../../../CircularLoader";
import EmptyCart from "../../../EmptyCart";
import { VerticalProgressBar } from "../../../ProgressBar";
import { IStep } from "../../../ProgressBar/VerticalProgressBar/VerticalProgressBar.component";
import PriceRecap from "../PriceRecap";

interface IWrapperProps {
  headerHeight: number;
}

interface IContentWrapper {
  headerHeight: number;
  windowHeight: number;
}

const ContentWrapper = styled.section<IContentWrapper & IWrapperProps>`
  height: ${(props: IContentWrapper): number =>
    props.windowHeight - props.headerHeight}px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${theme.colors.white};

  ${media.laptop} {
    height: initial;

    .price {
      margin-left: 20px;
    }
  }
`;

const Wrapper = styled.article<IWrapperProps>`
  display: flex;
  flex-direction: column;
  position: fixed;
  top: ${({ headerHeight }: IWrapperProps): string => `${headerHeight}px`};
  left: 0;
  right: 0;
  z-index: 30;

  ${media.laptop} {
    top: initial;
    left: initial;
    right: 0;
    margin-top: -8px;
    position: absolute;
    background-color: ${theme.colors.white};
    max-width: 450px;
    box-shadow: 4px 10px 30px rgba(0, 0, 0, 0.5);

    &.open {
      border: 1px solid #efefefef;
    }

    ${ContentWrapper} > div {
      padding-bottom: 20px;
    }

    &.has-product ${ContentWrapper} > div {
      padding-bottom: 0;
    }
  }
`;

const Body = styled.section`
  background-color: ${theme.colors.white};
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0;
  overflow-y: auto;

  ${media.laptop} {
    max-height: 400px;
  }
`;

const Footer = styled.footer`
  background-color: ${theme.colors.white};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: ${theme.margins.paddingPage} ${theme.margins.paddingPageXBig};

  ${media.laptop} {
    padding: 0;

    article {
      padding: ${theme.margins.paddingPageBig};
    }
  }
`;

const StyledProgressBar = styled(VerticalProgressBar)`
  align-items: flex-start;
  min-height: min-content;
  margin-bottom: 0;
`;

const StyledButton = styled(Button)`
  flex-grow: 1;
  margin: ${theme.margins.marginBig} ${theme.margins.marginBase} 0
    ${theme.margins.marginBase};

  ${media.laptop} {
    &[type="submit"] {
      background-color: ${theme.colors.redDesktop};
      box-shadow: 0 -3px 0 ${theme.colors.darkRedDesktop} inset;
      border-radius: 4px;
      padding: 0 ${theme.margins.paddingPage};
      margin: 0;
      bottom: initial;
      flex-grow: initial;
    }
  }
`;

const EmptyCartWrapper = styled.div`
  background-color: ${theme.colors.lightGrey};
  height: 100%;
`;

const SubLabelWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SubLabel = styled.div`
  margin: 0;
  font-size: 15px;
  font-weight: normal;
  color: ${theme.colors.black};
`;

const Separator = styled.div`
  flex-grow: 1;
  flex-shrink: 0;
  background-color: ${theme.colors.black};
  height: 1px;
  max-height: 1px;
  margin: 0 ${theme.margins.marginBig};
  text-align: center;

  ${media.laptop} {
    border-top: 1px dotted #999;
    background-color: initial;
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const StyledQuantityPicker = styled(QuantityPicker)`
  display: inline-block;
  height: initial;

  .options-wrapper {
    max-height: 140px;
  }
`;

const Remove = styled.span`
  text-decoration: underline;
  font-family: ${theme.fonts.regular};
  font-size: ${theme.fontSizes.medium};
  color: ${theme.colors.grey};
  margin-top: 3px;
  cursor: pointer;

  &:hover {
    color: ${theme.colors.darkGrey};
  }
`;

const ListTitle = styled.p`
  text-transform: uppercase;
  font-weight: 700;
  color: ${theme.colors.black};
  margin-bottom: 5px;
  padding: 0 ${theme.margins.paddingPageBig};
`;

const FooterBackground = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;

  ${media.laptop} {
    background-color: ${theme.colors.lightBrown};
    padding: 10px 20px;
  }
`;

const ListWrapper = styled.div`
  padding: 0 ${theme.margins.paddingPageBig};
`;

export interface IProps extends ICartProductUpdate, IProductRemove {
  cart: ICart | null;
  cartLoading: boolean;
  headerHeight: number;
  isOpen: boolean;
  toggleOpen: () => void;
  isCheckout: boolean;
  hasProduct?: boolean;
  renderOnProps?: IRenderOn;
}

interface IState {
  windowInnerHeight: number;
}

class OrderRecapBody extends PureComponent<IProps, IState> {
  public state: IState = {
    windowInnerHeight: window.innerHeight,
  };

  public componentDidMount(): void {
    window.addEventListener("resize", this.windowResize);
  }

  public componentWillUnmount(): void {
    window.removeEventListener("resize", this.windowResize);
  }

  public mapProductDetailsToSteps = (items: ICartProductDetail[]): IStep[] => {
    const { isCheckout, cartProductRemove } = this.props;

    return items.map(
      ({ product, quantity }: ICartProductDetail, i: number): IStep => ({
        imageSrc: product.image,
        label: product.name,
        price: formatPrice(product.actualPrice * quantity) + " €",
        productUrl: product.url,
        subLabel: (
          <SubLabelWrapper>
            <SubLabel>
              {isCheckout ? (
                `${translate("orderSummary.qty")}: ${quantity}`
              ) : (
                <>
                  {translate("orderSummary.qty") + " "}
                  <StyledQuantityPicker
                    className={classnames("quantity-select-order-recap-body", {
                      lastItem: i + 1 === items.length,
                    })}
                    quantity={quantity}
                    onSelect={(newQuantity: number): void => {
                      this.updateProductQuantity(product, newQuantity);
                    }}
                  />
                  <div>
                    <Remove
                      onClick={(): void => {
                        cartProductRemove({
                          variables: { input: { productId: product.id } },
                        });
                      }}
                    >
                      {translate("global.remove")}
                    </Remove>
                  </div>
                </>
              )}
            </SubLabel>
          </SubLabelWrapper>
        ),
      })
    );
  };

  public render(): ReactNode {
    const {
      headerHeight,
      isOpen,
      hasProduct,
      renderOnProps: { mdOrDown = false } = {},
    } = this.props;
    const { windowInnerHeight } = this.state;

    const preventClick = (e: React.MouseEvent): void => e.stopPropagation();

    return (
      <Wrapper
        className={classnames({ open: isOpen }, "order-recap", {
          "has-product": hasProduct,
        })}
        headerHeight={headerHeight}
        onClick={preventClick}
      >
        <ScrollLock isActive={isOpen} />
        <ToggleSlide open={isOpen} duration={mdOrDown ? 200 : 0}>
          <ContentWrapper
            headerHeight={headerHeight}
            windowHeight={windowInnerHeight}
          >
            {this.renderBody()}
          </ContentWrapper>
        </ToggleSlide>
      </Wrapper>
    );
  }

  public renderBody = (): ReactNode => {
    const {
      cart,
      cartLoading,
      toggleOpen,
      isCheckout,
      renderOnProps: { lgOrUp = false } = {},
    } = this.props;

    if (cartLoading) {
      return (
        <EmptyCartWrapper>
          <LoaderWrapper>
            <CircularLoader />
          </LoaderWrapper>
        </EmptyCartWrapper>
      );
    }

    return cart != null && cart.items.length > 0 ? (
      <>
        <TouchScrollable>
          {lgOrUp && (
            <ListTitle>{translate("cart.lastArticlesAdded")}</ListTitle>
          )}
          <Body>
            <ListWrapper>
              <StyledProgressBar
                dotType={lgOrUp ? "none" : "dot"}
                imageWidth={68}
                progressIndex={cart.items.length}
                steps={this.mapProductDetailsToSteps(cart.items)}
                onClickLink={toggleOpen}
              />
            </ListWrapper>
          </Body>
        </TouchScrollable>
        <Separator />
        <Footer>
          <PriceRecap
            coupon={cart.coupon}
            deliveryMode={cart.deliveryMode}
            estimatedShipping={cart.estimatedShipping}
            giftCards={cart.giftCards}
            items={cart.items}
            subTotal={cart.subTotal}
            taxAmount={cart.taxAmount}
            total={cartTotal(cart)}
          />
          <FooterBackground>
            <StyledButton
              onClick={
                isCheckout
                  ? toggleOpen
                  : (): void =>
                      lgOrUp
                        ? window.location.assign(
                            translate("links.checkout.cart")
                          )
                        : history.push(translate("links.checkout.cart"))
              }
              title={
                isCheckout
                  ? translate("orderSummary.close")
                  : translate("orderSummary.checkout")
              }
            />
          </FooterBackground>
        </Footer>
      </>
    ) : (
      <EmptyCartWrapper>
        <EmptyCart imageColor={theme.colors.black} onClick={toggleOpen} />
      </EmptyCartWrapper>
    );
  };

  public updateProductQuantity = (
    product: IProduct,
    quantity: number
  ): void => {
    const { cartProductUpdate } = this.props;
    cartProductUpdate({
      variables: { input: { productId: product.id, quantity } },
    });
  };

  public windowResize = (): void => {
    this.setState({ windowInnerHeight: window.innerHeight });
  };
}

export default withRenderOn(OrderRecapBody);
